import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { HangupCallEvent, RingingCallEvent, RingoverSDK } from './types';
// @ts-expect-error package has no types
import RingoverSDKLib from 'ringover-sdk';
import { captureException } from 'utils/exceptionUtils';
import { RINGOVER_CALL_UI_CONTAINER_ID } from 'components/Ringover/RingoverCallFloatingUI';
import { useAuth } from 'context/auth';
import CallIntegrationType from 'crono-fe-common/types/enums/callIntegrationType';
import { RingoverContext } from './contexts';

export const RingoverProvider: FC<PropsWithChildren> = ({ children }) => {
  const sdk = useRef<RingoverSDK>();
  const { user } = useAuth();
  const isRingoverEnabled =
    user?.callIntegrationType === CallIntegrationType.Ringover;
  const [isFloatingUIVisible, setIsFloatingUIVisible] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const onRingingRef = useRef<
    ((event: RingingCallEvent) => void) | undefined
  >();
  const onHangupRef = useRef<((event: HangupCallEvent) => void) | undefined>();

  useEffect(() => {
    try {
      if (!isRingoverEnabled) {
        return;
      }

      if (sdk.current) {
        console.warn('Ringover SDK is already initialized');
        return;
      }

      sdk.current = new RingoverSDKLib({
        size: 'auto',
        container: RINGOVER_CALL_UI_CONTAINER_ID,
        position: {
          top: null,
          bottom: null,
          left: null,
          right: null,
        },
        border: false,
        trayicon: false,
      }) as RingoverSDK;

      sdk.current.generate();
      sdk.current.hide();
      setIsFloatingUIVisible(false);

      sdk.current.on('ringingCall', (e) => {
        setIsFloatingUIVisible(true);
        setIsCallActive(true);
        if (e.data.direction === 'out') {
          onRingingRef.current?.(e);
        }
      });

      sdk.current.on('hangupCall', (e) => {
        setIsCallActive(false);
        if (e.data.direction === 'out') {
          // hangupCall fires for every outgoing call in the widget, so
          // consume the callbacks once the dialed call ends to avoid
          // replaying them on unrelated calls
          const onHangup = onHangupRef.current;
          onRingingRef.current = undefined;
          onHangupRef.current = undefined;
          onHangup?.(e);
        }
      });
    } catch (error) {
      console.error('Failed to initialize Ringover SDK:', error);
      captureException(error);
    }

    return () => {
      sdk?.current?.destroy();
      sdk.current = undefined;
    };
  }, [isRingoverEnabled]);

  const makeCall = (
    number: string | number,
    fromNumber?: string | number,
    onHangup?: (event: HangupCallEvent) => void,
    onRinging?: (event: RingingCallEvent) => void,
  ): boolean => {
    if (!sdk.current) {
      console.error('Ringover SDK is not initialized');
      captureException(
        new Error(
          `Tried calling ${number} but Ringover SDK is not initialized`,
        ),
      );
      return false;
    }

    onRingingRef.current = onRinging;
    onHangupRef.current = onHangup;

    const callResult = sdk.current.dial(number, fromNumber);
    if (!callResult) {
      console.error('Failed to dial number using Ringover SDK');
      captureException(
        new Error(`Failed to dial ${number} using Ringover SDK`),
      );
    }
    setIsFloatingUIVisible(true);
    return callResult;
  };

  const openFloatingUI = () => {
    if (!sdk.current) {
      console.error('Ringover SDK is not initialized');
      captureException(
        new Error(
          `Tried opening floating UI but Ringover SDK is not initialized`,
        ),
      );
      return;
    }

    sdk.current.show();
    setIsFloatingUIVisible(true);
  };

  const closeFloatingUI = () => {
    if (!sdk.current) {
      console.error('Ringover SDK is not initialized');
      captureException(
        new Error(
          `Tried closing floating UI but Ringover SDK is not initialized`,
        ),
      );
      return false;
    }

    sdk.current.hide();
    setIsFloatingUIVisible(false);
  };

  const value = {
    makeCall,
    openFloatingUI,
    closeFloatingUI,
    isFloatingUIVisible,
    isCallActive,
  };

  return (
    <RingoverContext.Provider value={value}>
      {children}
    </RingoverContext.Provider>
  );
};

export const useRingover = () => {
  const context = useContext(RingoverContext);
  if (context === undefined) {
    throw new Error('useRingover must be used within a RingoverProvider');
  }
  return context;
};
